import * as React from 'react';
import {LOGOS_NEEDING_DARK_TILE, isLogoId, logoUrl} from 'src/logoOptions';

type Props = {
  /** Logo slug, e.g. "verizon". Unknown or empty values render nothing. */
  logo: string | null | undefined;
  /** Tailwind size class, e.g. "size-4". Matches the ten existing call sites. */
  className?: string;
  /**
   * Corner rounding. A separate prop rather than something to pass through
   * className, because Tailwind resolves conflicting utilities by stylesheet
   * order, so a caller-supplied `rounded-full` would not reliably beat the
   * default here.
   */
  rounded?: string;
  alt?: string;
};

/**
 * Renders a brand logo on the solid tile it needs.
 *
 * Many logos are a transparent PNG rather than a self-contained icon, so they
 * need a background to stay legible: a dark mark disappears on a dark surface.
 * The tile is white by default, which is a no-op for the majority that carry
 * their own opaque background.
 *
 * A handful are the inverse — a light mark on transparency, invisible on white
 * — and get a dark tile instead. That set is generated into logoOptions.ts by
 * logos/backgrounds.mjs rather than hand-maintained, since it is derived from
 * the pixels of whatever is actually committed.
 */
export const LogoImage = ({
  logo,
  className = 'size-4',
  rounded = 'rounded-sm',
  alt = '',
}: Props) => {
  if (!isLogoId(logo)) return null;

  const tile = LOGOS_NEEDING_DARK_TILE.has(logo) ? 'bg-neutral-800' : 'bg-white';

  return (
    <img
      src={logoUrl(logo)}
      alt={alt}
      // Lazy: the picker can mount dozens of these at once, and eagerly
      // fetching every thumbnail on open is a lot of requests for a list the
      // user scrolls a few rows of.
      loading="lazy"
      // object-contain, never cover: a wordmark cropped to fill its box is
      // unreadable, and letterboxing inside the tile is the lesser evil.
      className={`${className} ${tile} ${rounded} shrink-0 object-contain`}
    />
  );
};
